import {Link, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../ui/forms/input-field/text-field/text-field';
import {Button} from '../../ui/buttons/button';
import {Form} from '../../ui/forms/form';
import {LoginPayload, useLogin} from '../requests/use-login';
import {FormSwitch} from '../../ui/forms/toggle/switch';
import {LoginVisualLayout} from './login-page/login-visual-layout';
import {Trans} from '../../i18n/trans';
import {EmailIcon} from '../../icons/material/Email';
import {LockIcon} from '../../icons/material/Lock';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useContext} from 'react';
import {
  SiteConfigContext,
  SiteConfigContextValue,
} from '../../core/settings/site-config-context';
import {useSettings} from '../../core/settings/use-settings';

interface Props {
  onTwoFactorChallenge: () => void;
}
export function LoginPage({onTwoFactorChallenge}: Props) {
  const [searchParams] = useSearchParams();
  const {pathname} = useLocation();

  const isWorkspaceLogin = pathname.includes('workspace');
  const searchParamsEmail = searchParams.get('email') || undefined;

  const {branding, registration, site} = useSettings();
  const siteConfig = useContext(SiteConfigContext);

  const demoDefaults =
    site.demo && !searchParamsEmail ? getDemoFormDefaults(siteConfig) : {};
  const form = useForm<LoginPayload>({
    defaultValues: {remember: true, email: searchParamsEmail, ...demoDefaults},
  });
  const login = useLogin(form);

  const heading = isWorkspaceLogin ? (
    <Trans
      values={{siteName: branding?.site_name}}
      message="To join your team on :siteName, login to your account"
    />
  ) : (
    <Trans message="Welcome back :)" />
  );

  const signupRoute = isWorkspaceLogin ? '/workspace/join/register' : '/checkout';

  const message = !registration.disable && (
    <Trans
      values={{
        a: parts => (
          <Link
            className="font-semibold text-[#FF4768] transition-colors hover:text-[#FF783F]"
            to={signupRoute}
          >
            {parts}
          </Link>
        ),
      }}
      message="Don't have an account? <a>Get started</a>"
    />
  );

  const isInvalid = !!Object.keys(form.formState.errors).length;

  return (
    <LoginVisualLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Login" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={payload => {
          login.mutate(payload, {
            onSuccess: response => {
              if (response.two_factor) {
                onTwoFactorChallenge();
              }
            },
          });
        }}
      >
        <div className="mb-28 space-y-24">
          <FormTextField
            className="[&_label>span]:hidden"
            name="email"
            type="email"
            label={<Trans message="Email" />}
            startAdornment={<EmailIcon className="text-[#7D809D]" />}
            background="bg-white"
            inputBorder="border border-[#252525]"
            inputShadow=""
            inputRadius="rounded-none"
            inputWrapperClassName="[&_input]:h-44"
            disabled={!!searchParamsEmail}
            invalid={isInvalid}
            required
          />
          <FormTextField
            className="[&_label>span]:hidden"
            name="password"
            type="password"
            label={<Trans message="Password" />}
            startAdornment={<LockIcon className="text-[#7D809D]" />}
            background="bg-white"
            inputBorder="border border-[#252525]"
            inputShadow=""
            inputRadius="rounded-none"
            inputWrapperClassName="[&_input]:h-44"
            invalid={isInvalid}
            required
          />
        </div>
        <div className="mb-34 flex flex-col gap-16 sm:flex-row sm:items-center sm:justify-between">
          <FormSwitch
            name="remember"
            className="[&_input:checked]:!border-[#00BFD0] [&_input:checked]:!bg-[#00BFD0]"
          >
            <Trans message="Stay signed in for a month" />
          </FormSwitch>
          <Link
            className="text-[#7D809D] transition-colors hover:text-[#5B5E77] hover:underline"
            to="/forgot-password"
            tabIndex={-1}
          >
            <Trans message="Forgot your password?" />
          </Link>
        </div>
        <Button
          className="block w-full rounded-[4px] border-transparent !border-transparent bg-gradient-to-r from-[#00BFD0] to-[#50A9CB] py-16 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_8px_22px_rgba(40,100,232,0.22)] transition-colors hover:brightness-95"
          type="submit"
          variant="flat"
          color="primary"
          size={null}
          disabled={login.isPending}
        >
          <Trans message="Login Now" />
        </Button>
      </Form>
    </LoginVisualLayout>
  );
}

function getDemoFormDefaults(siteConfig: SiteConfigContextValue) {
  if (siteConfig.demo.loginPageDefaults === 'randomAccount') {
    // random number between 0 and 100, padded to 3 digits
    const number = Math.floor(Math.random() * 100) + 1;
    const paddedNumber = String(number).padStart(3, '0');
    return {
      email: `admin@demo${paddedNumber}.com`,
      password: 'admin',
    };
  } else {
    return {
      email: siteConfig.demo.email ?? 'admin@admin.com',
      password: siteConfig.demo.password ?? 'admin',
    };
  }
}
